import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ClipboardList, QrCode, History, User, Wifi, WifiOff, CloudUpload, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/common";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { syncPending, pendingCount, onConnectivityChange, syncPlanillas, planillaPendingCount } from "@/lib/offline";

const NAV = [
  { to: "/app", end: true, icon: Home, label: "Inicio" },
  { to: "/app/tareas", icon: ListChecks, label: "Tareas" },
  { to: "/app/historial", icon: History, label: "Historial" },
  { to: "/app/perfil", icon: User, label: "Perfil" },
];

export default function UserLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);

  const refreshPending = async () => setPending((await pendingCount()) + (await planillaPendingCount()));

  useEffect(() => {
    refreshPending();
    const off = onConnectivityChange(async (isOnline) => {
      setOnline(isOnline);
      if (isOnline) {
        const a = await syncPending();
        const b = await syncPlanillas();
        const n = a.synced + b.synced;
        if (n > 0) toast.success(`${n} registro(s) sincronizado(s)`);
        refreshPending();
      }
    });
    Promise.all([syncPending(), syncPlanillas()]).then(([a, b]) => { const n = a.synced + b.synced; if (n > 0) toast.success(`${n} registro(s) sincronizado(s)`); refreshPending(); });
    const t = setInterval(refreshPending, 10000);
    return () => { off(); clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-white/90 px-4 py-3 backdrop-blur">
        <Brand subtitle={user?.organization_name} />
        <div className="flex items-center gap-1.5">
          {pending > 0 && (
            <span data-testid="sync-pending-badge" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
              <CloudUpload className="h-3.5 w-3.5" /> {pending}
            </span>
          )}
          <span data-testid="connectivity-badge" className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${online ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
            {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />} {online ? "En línea" : "Sin conexión"}
          </span>
          <NotificationBell />
        </div>
      </header>

      <main className="mx-auto max-w-md"><Outlet /></main>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t bg-white">
        <div className="mx-auto grid max-w-md grid-cols-5 items-end px-2 pb-2 pt-1.5">
          {NAV.slice(0, 2).map((n) => <BottomItem key={n.to} {...n} />)}
          <div className="flex justify-center">
            <button data-testid="scan-qr-button" onClick={() => nav("/app/escanear")}
              className="-mt-6 grid h-16 w-16 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform active:scale-95">
              <QrCode className="h-7 w-7" />
            </button>
          </div>
          {NAV.slice(2).map((n) => <BottomItem key={n.to} {...n} />)}
        </div>
        <p className="pb-1 text-center text-[10px] font-medium uppercase tracking-wider text-primary">Escanear QR</p>
      </nav>
    </div>
  );
}

function BottomItem({ to, end, icon: Icon, label }) {
  return (
    <NavLink to={to} end={end} data-testid={`bottom-nav-${label.toLowerCase()}`}
      className={({ isActive }) => `flex flex-col items-center gap-1 rounded-lg py-1.5 text-[11px] font-medium ${isActive ? "text-primary" : "text-slate-400"}`}>
      <Icon className="h-5 w-5" /> {label}
    </NavLink>
  );
}
